import './ArrowsWrapper.less';

import React, { useMemo, useRef } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { virtualize } from 'react-swipeable-views-utils';

import useAperture from '@/_hooks/useAperture';
import useArrows from '@/_hooks/useArrows';

export function ArrowsWrapper({ children }) {
    return children(false);
}

const VirtualizedSwipeableViews = virtualize(SwipeableViews);

export function ArrowsWrappedSwipeableViews({
    collection,
    apertureSize,
    children,
    useVirtualize = false,
    initialIndex = 0
}) {
    const [ref, aperture, rest, size] = useAperture(apertureSize);
    const sizeRef = useRef(size);
    const groups = useMemo(() => aperture(collection), [aperture, collection]);
    const { index, setIndex, leftDisabled, rightDisabled } = useArrows(0, groups.length);
    if (!sizeRef.current && size && initialIndex > 0) {
        setIndex(Math.floor(initialIndex / size));
    }
    // when collection size changes, index needs to change too
    // the easiest way is to reset it to 0
    if (sizeRef.current && sizeRef.current !== size) {
        setIndex(0);
    }
    sizeRef.current = size;
    const lastPossible = collection.length - 1;
    const Swipeable = useMemo(
        () => ({ children, index, ...props }) => {
            // we need this, because if size is zero, all items from collections are in one group
            // therefore all items are rendered within one react-swipeable-views' slide
            // and if the collection is big, this consumes a substantial amount of time
            if (!size) return null;
            if (useVirtualize) {
                return (
                    <VirtualizedSwipeableViews
                        index={index}
                        onChangeIndex={index => setIndex(index)}
                        resistance
                        disableLazyLoading
                        {...props}
                        slideCount={groups.length}
                        slideRenderer={({ index }) => {
                            if (groups[index]) {
                                return (
                                    // TODO: move inline style to CSS
                                    <div key={index} style={{ display: 'flex' }}>
                                        {groups[index].map((item, key) =>
                                            children(item, key, index * size + key === lastPossible)
                                        )}
                                    </div>
                                );
                            }
                        }}
                    />
                );
            }
            return (
                <SwipeableViews
                    index={index}
                    onChangeIndex={index => setIndex(index)}
                    resistance
                    disableLazyLoading
                    {...props}
                >
                    {groups.map((group, index) => (
                        // TODO: move inline style to CSS
                        <div key={index} style={{ display: 'flex' }}>
                            {group.map((item, key) => children(item, key, index * size + key === lastPossible))}
                        </div>
                    ))}
                </SwipeableViews>
            );
        },
        [groups, lastPossible, setIndex, size, useVirtualize]
    );
    Swipeable.displayName = 'Swipeable';
    return (
        <ArrowsWrapper
            onLeftClick={() => setIndex(index - 1)}
            onRightClick={() => setIndex(index + 1)}
            leftDisabled={leftDisabled}
            rightDisabled={rightDisabled}
        >
            {arrows => children(arrows, Swipeable, index, rest, ref, setIndex)}
        </ArrowsWrapper>
    );
}
